/**
 * Help steps (requirement 9.10): what each track's self-guided walkthrough is, and how the marker finds the thing
 * to click. The drawer that renders this lives in `components/HelpGuide.tsx`; the data is here, next to the
 * navigation registry it has to agree with, so it can be checked without a browser (`help.test.ts`).
 *
 * A track is one recurring business problem, not one screen — but every screen of the application belongs to
 * exactly one track, so the drawer is never empty on a page a person can reach (`trackForPath`). The step texts
 * live in the i18n bundles: `help.<track>.name`, `help.<track>.problem`, `help.<track>.sN`.
 *
 * "Show me" never navigates: it puts a marker on the element to click. A step lists its hops — the tab or row that
 * reveals the target, then the target itself — and the marker sits on the last hop visible on the current page,
 * following the person as the page changes. Off the step's page the marker points at the way there, and since the
 * modules are navigated by sub-navigation *links* now (see `components/SubNav.tsx`), the way there is derived from
 * the route rather than written out by hand: one hop per level the reader still has to open, then the hub card, the
 * ☰ Modules menu or an in-page link as fallbacks. A `look` target is something to read, not to click, so its step
 * is ticked by hand.
 *
 * Targets are `data-*` hooks wherever the screen has one (they survive copy changes and translation); a text finder
 * matches the translated label of a button, tab or heading and is used only where no hook exists.
 */
import { resolve } from "./nav";

export type Finder = {
  /** CSS selector, matched against the first visible element outside the drawer. */
  sel?: string;
  /** i18n key; matched against the translated label of a `role`/`tag` element (exact, then prefix, then contains). */
  text?: string;
  role?: string;
  tag?: string;
  /** A selector looked up inside the nearest container around the match — a row inside the section, say. */
  then?: string;
  /** Something to read rather than click: the step is ticked by hand. */
  look?: boolean;
  /** A link to the step's page that happens to sit on this page, rather than a navigation control. */
  inpage?: boolean;
};
export type Step = { key: string; to: string; hops: Finder[] };
export type Guide = { minutes: number; steps: Step[] };

// ---------------------------------------------------------------- finders
/** A tab of a page (`role="tab"`, still used where switching does not change the address). */
const tab = (key: string): Finder => ({ text: key, role: "tab" });
const btn = (key: string): Finder => ({ text: key, tag: "button" });
const h2 = (key: string): Finder => ({ text: key, tag: "h2", look: true });
/** Something to click. */
const go = (sel: string): Finder => ({ sel });
/** Something to read. */
const see = (sel: string): Finder => ({ sel, look: true });

export const GUIDES: Record<string, Guide> = {
  // ---------------------------------------------------------------- executive cockpit
  executive: { minutes: 8, steps: [
    { key: "s1", to: "/executive", hops: [h2("exec.exceptions")] },
    { key: "s2", to: "/executive", hops: [go("main button[data-exception]")] },
    { key: "s3", to: "/executive", hops: [go("main button[data-exception]"), btn("exec.detail.draftAi")] },
    { key: "s4", to: "/executive", hops: [go("main button[data-exception]"), btn("exec.detail.createAction")] },
    { key: "s5", to: "/executive", hops: [go("[data-scenario-model]")] },
    { key: "s6", to: "/executive", hops: [go("[data-export='pptx']")] },
  ] },
  finance: { minutes: 9, steps: [
    { key: "s1", to: "/finance", hops: [h2("fin.pnl.title")] },
    { key: "s2", to: "/finance", hops: [{ text: "fin.pnl.title", tag: "h2", then: "table tbody tr" }] },
    { key: "s3", to: "/finance", hops: [btn("fin.coa.approve")] },
    { key: "s4", to: "/finance", hops: [btn("fin.commentary.draft")] },
    { key: "s5", to: "/finance", hops: [btn("fin.controls.confirm")] },
    { key: "s6", to: "/finance", hops: [btn("fin.close.start"), btn("fin.close.markDone")] },
    // the document registers, which replaced the ERP screens in this application (owner's rule, 2026-09-28)
    { key: "s7", to: "/finance", hops: [go("[data-fin-documents]")] },
  ] },

  // ---------------------------------------------------------------- projects
  projects: { minutes: 9, steps: [
    // no record id in the address: the ids of the sample organisation change with every reload of the demo data,
    // and a step that points at a record that no longer exists marks nothing. The portfolio link is the first hop.
    { key: "s1", to: "/projects", hops: [go("main a[href^='/projects/PROJ-']")] },
    { key: "s2", to: "/projects", hops: [go("main a[href^='/projects/PROJ-']"), btn("prj.detail.delay.simulate")] },
    { key: "s3", to: "/projects", hops: [go("main a[href^='/projects/PROJ-']"), btn("prj.detail.delay.brief")] },
    { key: "s4", to: "/projects", hops: [go("main a[href^='/projects/PROJ-']"), btn("prj.detail.delay.approve")] },
    { key: "s5", to: "/projects", hops: [go("main a[href^='/projects/PROJ-']"), h2("prj.detail.milestones.title")] },
    { key: "s6", to: "/projects/sourcing", hops: [go("main a[href^='/projects/sourcing/SRC-']"), btn("src.narrate")] },
  ] },
  labour: { minutes: 7, steps: [
    { key: "s1", to: "/projects/labour", hops: [see("[data-labour-strip]")] },
    { key: "s2", to: "/projects/labour", hops: [see("[data-labour-row]")] },
    { key: "s3", to: "/projects/labour/approvals", hops: [go("[data-labour-scope-option]")] },
    { key: "s4", to: "/projects/labour/approvals", hops: [go("[data-labour-queue-row]"), go("[data-labour-approve]")] },
    { key: "s5", to: "/projects/labour/approvals", hops: [go("[data-labour-return]"), go("[data-labour-return-confirm]")] },
    { key: "s6", to: "/projects/labour/reconciliation", hops: [go("[data-labour-window]")] },
    { key: "s7", to: "/projects/labour/reconciliation", hops: [see("[data-labour-rec-row]")] },
  ] },

  // ---------------------------------------------------------------- plant
  operations: { minutes: 7, steps: [
    { key: "s1", to: "/operations", hops: [h2("ops.lines")] },
    { key: "s2", to: "/operations", hops: [tab("ops.tabs.downtime"), btn("ops.dt.log")] },
    { key: "s3", to: "/operations", hops: [tab("ops.tabs.factory")] },
    { key: "s4", to: "/operations", hops: [tab("ops.tabs.conditions"), btn("ops.acknowledge")] },
    { key: "s5", to: "/operations", hops: [tab("ops.tabs.labels"), btn("ops.lbl.verify")] },
    { key: "s6", to: "/executive", hops: [h2("exec.exceptions")] },
  ] },
  // rewritten on 2026-09-28: the track used to walk the ERPNext fleet board on `/fleet`, which the module's own
  // register replaced — OneFleet's vehicles, OneFleet's numbers, read with the reader's OneFleet permissions
  fleet: { minutes: 7, steps: [
    { key: "s1", to: "/fleet", hops: [see("[data-fleet-overview-kpi]")] },
    { key: "s2", to: "/fleet", hops: [see("[data-fleet-register-row]")] },
    { key: "s3", to: "/fleet", hops: [go("[data-fleet-register-all]")] },
    { key: "s4", to: "/fleet/vehicles", hops: [see("[data-fleet-vehicle-status]")] },
    { key: "s5", to: "/fleet/maintenance/schedules", hops: [see("[data-fleet-schedule-state]")] },
    { key: "s6", to: "/fleet/fuel", hops: [see("[data-fleet-fuel-anomalies]")] },
    { key: "s7", to: "/fleet/drivers", hops: [see("[data-fleet-drivers-expiring]")] },
  ] },
  contractors: { minutes: 7, steps: [
    { key: "s1", to: "/contractors", hops: [see("[data-con-stats]")] },
    { key: "s2", to: "/contractors", hops: [see("[data-con-access]")] },
    { key: "s3", to: "/contractors", hops: [see("[data-con-renewals]")] },
    { key: "s4", to: "/contractors", hops: [go("[data-con-row]")] },
    { key: "s5", to: "/contractors", hops: [go("[data-con-row]"), see("[data-con-gate]")] },
    { key: "s6", to: "/contractors", hops: [go("[data-con-row]"), go("[data-con-certify]")] },
    { key: "s7", to: "/contractors", hops: [go("[data-con-row]"), go("[data-con-post-timesheet]")] },
  ] },

  // ---------------------------------------------------------------- human capital, one track per week's work
  people: { minutes: 10, steps: [
    { key: "s1", to: "/people/directory", hops: [go("[data-employee-search]"), go("[data-employee-row]")] },
    { key: "s2", to: "/people/lifecycle", hops: [see("[data-hr-waiting]")] },
    { key: "s3", to: "/people/lifecycle/approvals", hops: [go("[data-hr-approve]")] },
    { key: "s4", to: "/people/lifecycle/gate", hops: [see("[data-hr-gate-row]")] },
    { key: "s5", to: "/people/lifecycle", hops: [see("[data-hr-boarding-row]")] },
    { key: "s6", to: "/people/lifecycle/movements", hops: [see("[data-hr-movement]")] },
    { key: "s7", to: "/people/lifecycle/exits", hops: [go("[data-hr-exit-open]"), go("[data-hr-exit-preview]")] },
    { key: "s8", to: "/people/assignments", hops: [go("[data-wfa-row]")] },
    { key: "s9", to: "/people/workforce", hops: [go("[data-workforce-search]"), go("[data-workforce-propose-open]")] },
  ] },
  hiring: { minutes: 12, steps: [
    { key: "s1", to: "/people/recruitment/request", hops: [go("[data-req-input='designation']"), go("[data-req-submit]")] },
    { key: "s2", to: "/people/lifecycle/approvals", hops: [see("[data-req-approvals]")] },
    { key: "s3", to: "/people/recruitment", hops: [see("[data-rec-openings]")] },
    { key: "s4", to: "/people/recruitment/channels", hops: [see("[data-rec-channel-row]")] },
    { key: "s5", to: "/people/recruitment/new", hops: [btn("rec.polish")] },
    { key: "s6", to: "/people/recruitment/new", hops: [go("[data-rec-channel-option]"), go("[data-rec-create]")] },
    { key: "s7", to: "/people/recruitment", hops: [go("[data-rec-opening-row]"), go("[data-rec-open]")] },
    { key: "s8", to: "/people/recruitment", hops: [go("[data-rec-opening-row]"), go("[data-rec-open]"), btn("rec.generateAi")] },
    { key: "s9", to: "/people/recruitment", hops: [go("[data-rec-opening-row]"), go("[data-rec-open]"), see("[data-iv-panel]")] },
    { key: "s10", to: "/people/recruitment", hops: [go("[data-rec-opening-row]"), go("[data-rec-open]"), { text: "rec.decisionTitle", tag: "div", look: true }] },
    { key: "s11", to: "/people/recruitment", hops: [go("[data-rec-opening-row]"), go("[data-rec-open]"), see("[data-offer-panel]")] },
    { key: "s12", to: "/people/recruitment/correspondence", hops: [see("[data-rec-thread]")] },
    { key: "s13", to: "/people/recruitment/paperwork", hops: [see("[data-hiring-offer]"), go("[data-hiring-build-letters]")] },
  ] },
  timeoff: { minutes: 8, steps: [
    { key: "s1", to: "/people/leave", hops: [see("[data-leave-check]"), go("[data-leave-fix]")] },
    { key: "s2", to: "/people/leave/accrual", hops: [see("[data-leave-accrual-strip]")] },
    { key: "s3", to: "/people/leave/compoff", hops: [go("[data-leave-comp-raise]")] },
    { key: "s4", to: "/people/expenses", hops: [see("[data-expenses-bucket]")] },
    { key: "s5", to: "/people/expenses", hops: [go("[data-expenses-pay]")] },
    { key: "s6", to: "/people/expenses/advances", hops: [go("[data-expenses-pay-advance]")] },
    { key: "s7", to: "/people/expenses/travel", hops: [see("[data-travel-finding]")] },
    { key: "s8", to: "/people/expenses/travel", hops: [see("[data-travel-request]"), go("[data-travel-draw]")] },
  ] },
  payroll: { minutes: 7, steps: [
    { key: "s1", to: "/people/payroll", hops: [see("[data-payroll-strip]")] },
    { key: "s2", to: "/people/payroll", hops: [see("[data-payroll-check]")] },
    { key: "s3", to: "/people/payroll/gratuity", hops: [see("[data-payroll-gratuity-row]")] },
    { key: "s4", to: "/people/payroll/gratuity", hops: [see("[data-payroll-gratuity-row]"), go("[data-payroll-raise]")] },
    { key: "s5", to: "/people/payroll/adjustments", hops: [see("[data-payroll-tax-summary]")] },
    { key: "s6", to: "/people/payroll/adjustments", hops: [see("[data-payroll-adjustment]")] },
    { key: "s7", to: "/people/lifecycle/payroll", hops: [see("[data-hr-payroll]")] },
  ] },
  growth: { minutes: 9, steps: [
    { key: "s1", to: "/people/appraisals", hops: [see("[data-appraisals-gap]")] },
    { key: "s2", to: "/people/appraisals", hops: [go("[data-appraisals-open]")] },
    { key: "s3", to: "/people/appraisals/scoring", hops: [see("[data-appraisals-check]")] },
    { key: "s4", to: "/people/learning", hops: [h2("ppl.certAttention")] },
    { key: "s5", to: "/people/learning/programmes", hops: [see("[data-ld-programmes]")] },
    { key: "s6", to: "/people/learning/needs", hops: [see("[data-ld-needs]")] },
    { key: "s7", to: "/people/learning/outcomes", hops: [go("[data-training-open]"), go("[data-training-uplift]")] },
    { key: "s8", to: "/people/learning/matrix", hops: [see("[data-ld-matrix]")] },
    { key: "s9", to: "/people/learning/succession", hops: [see("[data-ld-succession]")] },
  ] },
  records: { minutes: 8, steps: [
    { key: "s1", to: "/people/files", hops: [see("[data-files-finding]")] },
    { key: "s2", to: "/people/files", hops: [go("[data-files-employee]")] },
    { key: "s3", to: "/people/files", hops: [see("[data-files-master]")] },
    { key: "s4", to: "/people/conduct", hops: [see("[data-conduct-grievance]")] },
    { key: "s5", to: "/people/conduct", hops: [see("[data-conduct-case]")] },
    { key: "s6", to: "/people/conduct", hops: [see("[data-conduct-evidence]")] },
    { key: "s7", to: "/people/compliance", hops: [go("[data-compliance-row]"), go("[data-compliance-view]")] },
    { key: "s8", to: "/people/compliance", hops: [go("[data-compliance-audit-pack]")] },
  ] },

  // ---------------------------------------------------------------- commercial
  orders: { minutes: 8, steps: [
    { key: "s1", to: "/orders?tab=pipeline", hops: [see("[data-o2c-stats]")] },
    { key: "s2", to: "/orders?tab=orders", hops: [tab("o2c.tabs.orders"), go("[data-o2c-create]")] },
    { key: "s3", to: "/orders?tab=orders", hops: [tab("o2c.tabs.orders"), go("[data-o2c-action]")] },
    { key: "s4", to: "/orders?tab=collections", hops: [tab("o2c.tabs.collections"), go("[data-o2c-customer]"), go("[data-o2c-reminder-ai]")] },
    { key: "s5", to: "/orders?tab=incentives&period=2026-08", hops: [tab("o2c.tabs.incentives"), go("[data-o2c-open-statement]")] },
    { key: "s6", to: "/orders?tab=incentives&period=2026-08", hops: [tab("o2c.tabs.incentives"), go("[data-o2c-open-statement]"), go("[data-o2c-brief-ai]")] },
  ] },
  pos: { minutes: 5, steps: [
    { key: "s1", to: "/pos", hops: [see("[data-pos-session]"), go("[data-pos-open]")] },
    { key: "s2", to: "/pos", hops: [go("[data-pos-search]"), go("[data-pos-item]")] },
    { key: "s3", to: "/pos", hops: [go("[data-pos-mode]")] },
    { key: "s4", to: "/pos", hops: [go("[data-pos-charge]")] },
    { key: "s5", to: "/pos", hops: [see("[data-pos-rollup]")] },
    { key: "s6", to: "/pos", hops: [see("[data-pos-recent]")] },
  ] },
  procure: { minutes: 8, steps: [
    { key: "s1", to: "/procure?tab=pipeline", hops: [see("[data-p2p-stats]")] },
    { key: "s2", to: "/procure?tab=requests", hops: [tab("p2p.tabs.requests"), go("[data-p2p-new-request]"), go("[data-p2p-create]")] },
    { key: "s3", to: "/procure?tab=suppliers", hops: [tab("p2p.tabs.suppliers"), go("[data-p2p-onboarding]"), go("[data-p2p-onboarding] a, [data-p2p-onboarding] button")] },
    { key: "s4", to: "/procure?tab=suppliers", hops: [see("[data-p2p-suppliers]"), go("[data-p2p-onboarding] a, [data-p2p-onboarding] button"), go("[data-p2p-checklist]")] },
    { key: "s5", to: "/projects/sourcing", hops: [go("main a[href^='/projects/sourcing/SRC-']"), go("[data-p2p-action]")] },
    { key: "s6", to: "/procure?tab=register", hops: [tab("p2p.tabs.register"), see("[data-p2p-register]")] },
  ] },

  // ---------------------------------------------------------------- cross-cutting
  my: { minutes: 5, steps: [
    { key: "s1", to: "/my", hops: [see("[data-my-identity]")] },
    { key: "s2", to: "/my", hops: [see("[data-my-work]")] },
    { key: "s3", to: "/my", hops: [go("[data-my-block] a")] },
    { key: "s4", to: "/my", hops: [see("[data-my-modules]")] },
    { key: "s5", to: "/my", hops: [tab("my.selfService"), go("[data-timeclock-punch]")] },
    { key: "s6", to: "/my", hops: [tab("my.selfService"), go("[data-ess-apply-leave]")] },
    { key: "s7", to: "/my", hops: [tab("my.selfService"), see("[data-ess-payslip]")] },
  ] },
  admin: { minutes: 7, steps: [
    { key: "s1", to: "/admin?tab=consoles", hops: [see("[data-admin-consoles]")] },
    { key: "s2", to: "/admin?tab=config", hops: [tab("admin.tabs.config"), go("[data-admin-config-group]")] },
    { key: "s3", to: "/admin?tab=config", hops: [tab("admin.tabs.config"), go("[data-employee-link-open]")] },
    { key: "s4", to: "/admin?tab=notifications", hops: [tab("admin.tabs.notifications"), go("[data-admin-notif-toggle]")] },
    { key: "s5", to: "/admin?tab=notifications", hops: [tab("admin.tabs.notifications"), go("[data-admin-notif-test]")] },
    { key: "s6", to: "/admin?tab=approvals", hops: [tab("admin.tabs.approvals"), go("[data-admin-appr-toggle]")] },
    { key: "s7", to: "/admin?tab=roles", hops: [tab("admin.tabs.roles"), see("[data-admin-roles]")] },
    { key: "s8", to: "/admin?tab=ai", hops: [tab("admin.tabs.ai"), see("[data-admin-ai]")] },
  ] },
};

/**
 * Which track a location belongs to — longest prefix wins, so `/projects/labour` is the labour track while
 * `/projects/sourcing` stays with Projects. Every route of `App.tsx` is covered: a person is never on a page
 * where the Help steps button silently fails to appear.
 */
export const TRACK_ROUTES: readonly (readonly [string, string])[] = [
  ["/executive", "executive"], ["/demo-metrics", "executive"], ["/process", "executive"], ["/reports", "executive"],
  ["/finance", "finance"],
  ["/projects", "projects"], ["/projects/labour", "labour"],
  // the sourcing screens live under /projects but belong to the Procurement module in `nav.ts`, which is what the
  // breadcrumb over them says; the drawer follows the registry rather than the path so the two agree
  ["/projects/sourcing", "procure"],
  ["/operations", "operations"], ["/fleet", "fleet"], ["/contractors", "contractors"],
  ["/people", "people"],
  ["/people/recruitment", "hiring"],
  ["/people/leave", "timeoff"], ["/people/expenses", "timeoff"],
  ["/people/payroll", "payroll"],
  ["/people/appraisals", "growth"], ["/people/learning", "growth"],
  ["/people/conduct", "records"], ["/people/files", "records"], ["/people/compliance", "records"],
  ["/orders", "orders"], ["/pos", "pos"], ["/procure", "procure"],
  ["/my", "my"], ["/admin", "admin"],
];

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

export function trackForPath(pathname: string): string | null {
  let best: readonly [string, string] | null = null;
  for (const route of TRACK_ROUTES) {
    if (under(pathname, route[0]) && (!best || route[0].length > best[0].length)) best = route;
  }
  return best && GUIDES[best[1]] ? best[1] : null;
}

export function pathOf(to: string): string {
  return to.split("?")[0];
}
export function searchOf(to: string): string {
  const q = to.split("?")[1];
  return q ? "?" + q : "";
}

/**
 * The sub-navigation links between here and the step's page: one hop per level the reader still has to open.
 * `/people/directory` → `/people/lifecycle/approvals` is two clicks — the *HR lifecycle* link of the People
 * sub-navigation, then *Approvals* in the one it reveals — and this returns them in that order. Matching is by
 * href rather than by the item key alone, because the same key appears at two levels (People has a *Payroll*
 * screen and so does HR lifecycle) and the marker must not land on the wrong one.
 */
export function subNavHops(to: string, pathname: string): Finder[] {
  const segs = pathOf(to).split("/").filter(Boolean);
  const here = pathname.split("/").filter(Boolean);
  const hops: Finder[] = [];
  for (let i = 2; i <= segs.length; i++) {
    if (here.slice(0, i).join("/") === segs.slice(0, i).join("/")) continue;   // this level is already open
    const path = "/" + segs.slice(0, i).join("/");
    hops.push({ sel: `a[data-subnav-item][href='${path}'], a[data-subnav-item][href^='${path}?']` });
  }
  return hops;
}

/**
 * How to reach a step's page from here, least to most preferred (the marker takes the last one it can see):
 * the ☰ Modules menu, the landing hub's cards, a link to the page on the page itself, then the module's own
 * sub-navigation — which is the nearest thing to hand whenever the reader is already inside the module.
 */
export function navHops(to: string, pathname: string): Finder[] {
  const r = resolve(pathOf(to), searchOf(to));
  const moduleTo = r?.module.to ?? pathOf(to);
  const entityTo = r?.entity?.to;
  const hops: Finder[] = [
    { sel: "[data-module-menu] summary" },
    { sel: `[data-module-menu] a[href="${moduleTo}"]` },
    { sel: `[data-landing] a[href="${moduleTo}"]` },
  ];
  if (entityTo && entityTo !== moduleTo) {
    hops.push({ sel: `[data-module-menu] a[href="${entityTo}"]` }, { sel: `[data-landing] a[href="${entityTo}"]` });
  }
  hops.push(
    { sel: `[data-module-menu] a[href="${to}"]` },
    { sel: `[data-landing] a[href="${to}"]` },
    // a link to the page itself on the current page (a project in the portfolio, a file in the onboarding list)
    { sel: `main a[href="${pathOf(to)}"], main a[href="${to}"]`, inpage: true },
    ...subNavHops(to, pathname),
  );
  return hops;
}
